import React, { useRef, useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';

export default function SidebarWorkspaceSelector({ 
  workspaces, 
  currentWorkspace, 
  onSwitchWorkspace, 
  saldo, 
  isMobile, 
  menuAberto 
}) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const isDark = theme === 'dark';

  // Formatar valor monetário
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!currentWorkspace) return null;

  // Cores do tema
  const colors = {
    bg: isDark ? 'var(--bg-secondary)' : 'var(--bg-secondary)',
    bgCard: isDark ? 'var(--bg-card)' : 'var(--bg-card)',
    border: isDark ? 'var(--border-primary)' : 'var(--border-primary)',
    textPrimary: isDark ? 'var(--text-primary)' : 'var(--text-primary)',
    textSecondary: isDark ? 'var(--text-secondary)' : 'var(--text-secondary)',
    hover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    active: isDark ? 'rgba(8, 145, 178, 0.2)' : '#f0fdfa',
  };

  // Quando menu está fechado (collapsed)
  if (!menuAberto && !isMobile) {
    return (
      <div style={{
        padding: '8px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        borderBottom: `1px solid ${colors.border}`,
        marginBottom: '8px'
      }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '44px',
            height: '44px',
            backgroundColor: currentWorkspace.cor_primaria || '#0891B2',
            border: 'none',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative'
          }}
          title={currentWorkspace.nome}
        >
          <span style={{ color: 'white', fontSize: '16px', fontWeight: '700' }}>
            {currentWorkspace.nome?.charAt(0)?.toUpperCase()}
          </span>
          {workspaces.length > 1 && (
            <div style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              width: '14px',
              height: '14px',
              backgroundColor: '#10b981',
              borderRadius: '50%',
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ fontSize: '8px', color: 'white', fontWeight: 'bold' }}>
                {workspaces.length}
              </span>
            </div>
          )}
        </button>
        
        {/* Dropdown compacto */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            left: '70px',
            top: '80px',
            width: '220px',
            backgroundColor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 200
          }}>
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => {
                  onSwitchWorkspace(workspace);
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: workspace.id === currentWorkspace.id ? colors.active : 'transparent',
                  border: 'none',
                  borderBottom: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  color: colors.textPrimary
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  backgroundColor: workspace.cor_primaria || '#0891B2',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{ color: 'white', fontSize: '11px', fontWeight: '600' }}>
                    {workspace.nome?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <span style={{ fontSize: '12px', fontWeight: 500 }}>{workspace.nome}</span>
              </button>
            ))}
            <button
              onClick={() => { setIsOpen(false); navigate('/app/workspaces/novo'); }}
              style={{
                width: '100%',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                backgroundColor: 'transparent',
                border: 'none',
                borderTop: `1px dashed ${colors.border}`,
                cursor: 'pointer',
                color: '#0891B2',
                fontSize: '11px'
              }}
            >
              <Icon icon="mdi:plus" width="14" />
              Nova Empresa
            </button>
          </div>
        )}
      </div>
    );
  }

  // Quando menu está aberto
  return (
    <div 
      ref={containerRef}
      style={{
        padding: '8px 16px 16px',
        borderBottom: `1px solid ${colors.border}`,
        marginBottom: '8px'
      }}
    >
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '100%',
            padding: '10px 12px',
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'}
        >
          {/* Avatar/Logo */}
          <div style={{
            width: '36px',
            height: '36px',
            backgroundColor: currentWorkspace.cor_primaria || '#0891B2',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            {currentWorkspace.logo_url ? (
              <img 
                src={currentWorkspace.logo_url} 
                alt="" 
                style={{ width: '22px', height: '22px', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '700' }}>
                {currentWorkspace.nome?.charAt(0)?.toUpperCase()}
              </span>
            )}
          </div>

          {/* Info */}
          <div style={{ flex: 1, textAlign: 'left', overflow: 'hidden' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: colors.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentWorkspace.nome}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#0891B2',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Icon icon="mdi:wallet" width="12" />
              {formatCurrency(saldo?.saldo_disponivel || saldo?.disponivel)}
              {workspaces.length > 1 && (
                <span style={{ color: colors.textSecondary, marginLeft: '4px' }}>
                  • {workspaces.length} empresas
                </span>
              )}
            </div>
          </div>

          <Icon 
            icon={isOpen ? "mdi:chevron-up" : "mdi:chevron-down"} 
            width="18" 
            height="18" 
            color={colors.textSecondary} 
          />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            backgroundColor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            zIndex: 200,
            maxHeight: '320px',
            overflow: 'auto'
          }}>
            {/* Header do dropdown */}
            <div style={{
              padding: '10px 12px',
              borderBottom: `1px solid ${colors.border}`,
              fontSize: '10px',
              fontWeight: '600',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Suas Empresas
            </div>

            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => {
                  onSwitchWorkspace(workspace);
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  backgroundColor: workspace.id === currentWorkspace.id ? colors.active : 'transparent',
                  border: 'none',
                  borderBottom: `1px solid ${colors.border}`,
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (workspace.id !== currentWorkspace.id) {
                    e.currentTarget.style.backgroundColor = colors.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (workspace.id !== currentWorkspace.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: workspace.cor_primaria || '#0891B2',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ color: 'white', fontSize: '12px', fontWeight: '700' }}>
                    {workspace.nome?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: colors.textPrimary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {workspace.nome}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: colors.textSecondary
                  }}>
                    {workspace.cnpj ? `CNPJ: ${workspace.cnpj}` : workspace.cpf ? `CPF: ${workspace.cpf}` : 'Documento não informado'}
                  </div>
                </div>
                {workspace.id === currentWorkspace.id && (
                  <Icon icon="mdi:check-circle" width="18" height="18" color="#0891B2" />
                )}
              </button>
            ))}

            {/* Criar nova empresa */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/app/workspaces/novo');
              }}
              style={{
                width: '100%',
                padding: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                backgroundColor: 'transparent',
                border: 'none',
                borderTop: `1px dashed ${colors.border}`,
                cursor: 'pointer',
                color: '#0891B2',
                fontSize: '13px',
                fontWeight: '600'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.hover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Icon icon="mdi:plus-circle" width="18" />
              Adicionar Nova Empresa
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
